/**
 * Unregisters every service worker for this origin and clears the PWA's
 * Cache Storage — the two layers responsible for a till looking "stuck on
 * an old version" even after a new deploy and a page reload (a plain
 * updateServiceWorker() call trusts Workbox's own skipWaiting/reload
 * sequence, which is exactly what can leave a wedged registration or a
 * stale precache entry in place). This destructive cache reset is reserved
 * for the explicit "Reset This Device" support action. Normal PWA updates
 * use Workbox's atomic waiting-worker handoff and never call this.
 */
export async function clearServiceWorkerCaches(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // Best effort — proceed with whatever else can still be cleared.
  }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // ignore
  }
}

/**
 * Full local reset for a stuck/misbehaving till — an escape hatch for
 * non-technical staff who'd otherwise need to be walked through clearing
 * browser data by hand. Covers every layer of local state this app can
 * accumulate:
 *  - Service worker + Cache Storage (see clearServiceWorkerCaches() above).
 *  - Clears IndexedDB — defensive: nothing here currently persists data
 *    there (Electric's synced collections are in-memory only, re-synced
 *    fresh each load), but cheap to cover in case that changes later, and
 *    harmless if there's nothing to delete.
 *  - Clears localStorage/sessionStorage — device pairing, staff session,
 *    held sales, cash drawer close-out, preferences (see local-store.ts).
 */
export async function resetDeviceData(): Promise<void> {
  await clearServiceWorkerCaches();
  try {
    if ("indexedDB" in window && indexedDB.databases) {
      const dbs = await indexedDB.databases();
      await Promise.all(
        dbs
          .filter((db): db is IDBDatabaseInfo & { name: string } => !!db.name)
          .map(
            (db) =>
              new Promise<void>((resolve) => {
                const req = indexedDB.deleteDatabase(db.name);
                req.onsuccess = () => resolve();
                req.onerror = () => resolve();
                req.onblocked = () => resolve();
              }),
          ),
      );
    }
  } catch {
    // Not every browser supports indexedDB.databases() — nothing to enumerate, so nothing lost.
  }
  try {
    localStorage.clear();
  } catch {
    // ignore
  }
  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }
}

/** Runs resetDeviceData() then does a real, full-navigation reload (not a
 * client-side route change) so the browser re-requests everything from
 * scratch against whatever's actually live now. */
export async function resetDeviceAndReload(): Promise<void> {
  await resetDeviceData();
  window.location.reload();
}
